import React from 'react';
import { motion } from 'framer-motion';
import { Router, Home } from 'lucide-react';
import { useWirelessAdvisor } from '../../context/WirelessAdvisorContext';
import QuestionTooltip from '../shared/QuestionTooltip';
import { trackEvent } from '../../../../utils/analytics';

/**
 * RouterLocation component
 * Second step in the wireless advisor form
 * Allows users to select their router location
 */
const RouterLocation = () => {
  const { formData, updateFormData } = useWirelessAdvisor();
  
  // Common router locations
  const routerLocations = [
    { id: 'livingRoom', label: 'Living Room', icon: <Home className="h-5 w-5" /> },
    { id: 'office', label: 'Home Office', icon: <Home className="h-5 w-5" /> },
    { id: 'bedroom', label: 'Bedroom', icon: <Home className="h-5 w-5" /> },
    { id: 'kitchen', label: 'Kitchen', icon: <Home className="h-5 w-5" /> },
    { id: 'hallway', label: 'Hallway', icon: <Home className="h-5 w-5" /> },
    { id: 'basement', label: 'Basement', icon: <Home className="h-5 w-5" /> },
    { id: 'custom', label: 'Other Location', icon: <Home className="h-5 w-5" /> }
  ];
  
  // Handle router location selection
  const handleLocationSelect = (locationId) => {
    // Update form data
    updateFormData({
      routerLocation: locationId
    });
    
    // Track event
    trackEvent('wireless_advisor_router_location_selected', {
      location: locationId
    });
  };
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Where is your router located?</h2>
        <QuestionTooltip 
          content="Router placement significantly affects your wireless coverage. Select where your router is currently located or where you plan to place it."
          iconSize="md"
        />
      </div>
      
      <p className="text-gray-400 text-sm">
        Choose the location of your router or modem. This helps us calculate signal coverage throughout your home.
      </p>
      
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {routerLocations.map((location) => (
          <motion.button
            key={location.id}
            onClick={() => handleLocationSelect(location.id)}
            className={`p-4 rounded-xl border-2 transition-all ${
              formData.routerLocation === location.id
                ? 'border-purple-600 bg-purple-600/10'
                : 'border-gray-700 hover:border-purple-500/50 bg-gray-800/50'
            }`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <div className="flex flex-col items-center text-center">
              <div className={`p-2 rounded-lg ${formData.routerLocation === location.id ? 'bg-purple-600/20' : 'bg-gray-700'} mb-2`}>
                {React.cloneElement(location.icon, { 
                  className: `h-5 w-5 ${formData.routerLocation === location.id ? 'text-purple-400' : 'text-gray-400'}`
                })}
              </div>
              <span className={`font-medium ${formData.routerLocation === location.id ? 'text-purple-400' : 'text-white'}`}>
                {location.label}
              </span>
            </div>
          </motion.button>
        ))}
      </div>
      
      {formData.routerLocation === 'custom' && (
        <motion.div
          className="mt-4"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ duration: 0.3 }}
        >
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Specify your router location:
          </label>
          <input
            type="text"
            className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="e.g., Upstairs closet"
            value={formData.customRouterLocation || ''}
            onChange={(e) => updateFormData({ customRouterLocation: e.target.value })}
          />
        </motion.div>
      )}
      
      {formData.routerLocation && formData.routerLocation !== 'custom' && (
        <motion.div
          className="mt-6 p-4 bg-purple-600/10 border border-purple-600/20 rounded-lg"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <h3 className="text-lg font-medium text-purple-400 mb-2">
            Router in {routerLocations.find(loc => loc.id === formData.routerLocation)?.label}
          </h3>
          <p className="text-sm text-gray-300">
            {formData.routerLocation === 'livingRoom' && 
              "Living rooms are often central in homes, which can provide good overall coverage. However, entertainment centers and TVs may cause interference."}
            {formData.routerLocation === 'office' && 
              "Home offices are good locations for routers, especially if you need reliable connectivity for work. Consider the distance to other rooms that need coverage."}
            {formData.routerLocation === 'bedroom' && 
              "Bedrooms can work well for router placement, but may not be central enough for whole-home coverage depending on your home's layout."}
            {formData.routerLocation === 'kitchen' && 
              "Kitchens have many appliances that can cause interference with WiFi signals. If possible, keep the router away from microwave ovens and refrigerators."}
            {formData.routerLocation === 'hallway' && 
              "Hallways can be good central locations for routers, providing even coverage throughout your home."}
            {formData.routerLocation === 'basement' && 
              "Basements can limit upward signal propagation. If your router must be in the basement, consider additional access points for upper floors."}
          </p>
        </motion.div>
      )}
    </div>
  );
};

export default RouterLocation;